/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Modal } from 'react-bootstrap'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useQuery } from 'react-query'
import { getCompanyOne, putCompanyOne } from '../../../../../../services/companyService'
import { putSettingsCompanyAll, postSettingsCompanyOne } from '../../../../../../services/settingsCompanyService'
import { postFile } from '../../../../../../services/files'
import { getTemplateAll } from '../../../../../../services/templates'
import { getTemplateOSAll } from '../../../../../../services/templateOS'
import { getTemplateProposalAll } from '../../../../../../services/templatesProposal'
import { useToast } from '../../../../../components/Toats'
import { getContrastColor } from '../../../../../../utils/contratColor'
import { KTSVG } from '../../../../../../../_metronic/helpers'
import { LoadingSpan } from '../../../../../components/Loadings/loadingSpan'
import { getUsersAllCompany, updateUsersAll } from '../../../../../../services/usersService'
import { initialLetters } from '../../../../../../utils/initialLetters'
import styles from './editCompany.module.css'

type Props = {
  id?: string
  data?: any
  show: boolean
  handleClose: () => void
  initialTab?: 'edit' | 'users'
}

const validationSchema = Yup.object().shape({
  company: Yup.object({
    corporateName: Yup.string().required(),
    cnpj: Yup.string().required(),
  }),
})

const initialValues = {
  company: {
    corporateName: '',
    cnpj: '',
    site: '',
    telephone: '',
    image: '',
    image_dark: '',
    image_cell: '',
    color_primary: '#ffc700',
    color_primary_variet: '#f1bc00',
  },
  plan: {
    qtd_plants_plano: 0,
    qtd_plants_plano_starter: 0,
    qtd_plants_gratuita: 0,
  },
  settings: {
    reports_client_automatics: true,
    // TODO: reativar módulo WhatsApp
    // liberar_whatsapp: true,
    liberar_whatsapp: false,
    idTemplateGeracao: '',
    idTemplateDetalhado: '',
    idTemplateOS: '',
    idTemplateProposal: '',
  },
  user: {
    id: '',
    name: '',
    email: '',
    account_type: 'NORMAL',
    qtd_dias_teste: 30,
  },
}

const maskCnpj = (value: string) =>
  value
    .replace(/\D/g, '')
    .slice(0, 14)
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2')

const modalsRoot = document.getElementById('root-modals') || document.body

const ModalUpdateCompany = ({ show, handleClose, id, data, initialTab = 'edit' }: Props) => {
  const { toggleToast } = useToast()
  const [loading, setLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [settingsCompanyId, setSettingsCompanyId] = useState<string | null>(null)
  const [logoPreview, setLogoPreview] = useState('')
  const [logoDarkPreview, setLogoDarkPreview] = useState('')
  const [logoCellPreview, setLogoCellPreview] = useState('')
  const [uploadingLogo, setUploadingLogo] = useState<'light' | 'dark' | 'cell' | null>(null)
  const [templates, setTemplates] = useState<any[]>([])
  const [templatesOS, setTemplatesOS] = useState<any[]>([])
  const [templatesProposal, setTemplatesProposal] = useState<any[]>([])
  const [activeTab, setActiveTab] = useState<'edit' | 'users'>(initialTab)
  const [users, setUsers] = useState<any[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [companyStatus, setCompanyStatus] = useState(data?.status || '')
  const [actionMessage, setActionMessage] = useState('')
  const [originalDemoDays, setOriginalDemoDays] = useState(0)
  const [originalExpires, setOriginalExpires] = useState<string | null>(null)

  useEffect(() => {
    if (!actionMessage) return
    const timeout = setTimeout(() => setActionMessage(''), 5000)
    return () => clearTimeout(timeout)
  }, [actionMessage])

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: false,
    onSubmit: () => {},
  })

  const loadTemplates = async () => {
    try {
      const [reports, os, proposals] = await Promise.all([
        getTemplateAll('public'),
        getTemplateOSAll('public'),
        getTemplateProposalAll('public'),
      ])
      setTemplates(Array.isArray(reports) ? reports : [])
      setTemplatesOS(Array.isArray(os) ? os : [])
      setTemplatesProposal(Array.isArray(proposals) ? proposals : [])
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    loadTemplates()
  }, [])

  const { isLoading: loadingCompany } = useQuery<any>(
    ['getMyCompanyEdit', id, show],
    async () => {
      if (!id) return null

      const myCompany = await getCompanyOne(id)
      const companyUsers = await getUsersAllCompany(id)
      const usersList = Array.isArray(companyUsers) ? companyUsers : []
      const empresaUser = usersList.find((user: any) => user.role?.slug === 'EMPRESA')
      const settingsCompany = myCompany && myCompany.settingsCompany ? myCompany.settingsCompany : null

      const findTemplateId = (list: any[], type?: string) => {
        if (!list || list.length === 0) return ''
        const found = type ? list.find((template: any) => template.type_template === type) : list[0]
        return found ? found.id : ''
      }

      setSettingsCompanyId(settingsCompany ? settingsCompany.id : null)
      setLogoPreview(myCompany && myCompany.image_url ? myCompany.image_url : '')
      setLogoDarkPreview(myCompany && myCompany.image_dark_url ? myCompany.image_dark_url : '')
      setLogoCellPreview(myCompany && myCompany.image_cell_url ? myCompany.image_cell_url : '')
      setCompanyStatus(myCompany?.status || '')
      setUsers(usersList)
      setOriginalDemoDays(Number(empresaUser?.qtd_dias_teste) || 0)
      setOriginalExpires(empresaUser?.data_expiracao || null)

      formik.setValues({
        company: {
          corporateName: myCompany?.corporateName || '',
          cnpj: myCompany?.cnpj || '',
          site: myCompany?.site || '',
          telephone: myCompany?.telephone || '',
          image: myCompany?.image || '',
          image_dark: myCompany?.image_dark || '',
          image_cell: myCompany?.image_cell || '',
          color_primary: myCompany?.color_primary || '#ffc700',
          color_primary_variet: myCompany?.color_primary_variet || '#f1bc00',
        },
        plan: {
          qtd_plants_plano: myCompany?.planosCompany?.qtd_plants_plano || 0,
          qtd_plants_plano_starter: myCompany?.planosCompany?.qtd_plants_plano_starter || 0,
          qtd_plants_gratuita: myCompany?.planosCompany?.qtd_plants_gratuita || 0,
        },
        settings: {
          reports_client_automatics: settingsCompany ? !!settingsCompany.reports_client_automatics : true,
          // TODO: reativar módulo WhatsApp
          // liberar_whatsapp: settingsCompany ? !!settingsCompany.liberar_whatsapp : true,
          liberar_whatsapp: false,
          idTemplateGeracao: findTemplateId(settingsCompany?.templates, 'GERACAO'),
          idTemplateDetalhado: findTemplateId(settingsCompany?.templates, 'DETALHADO'),
          idTemplateOS: findTemplateId(settingsCompany?.templatesOS),
          idTemplateProposal: findTemplateId(settingsCompany?.templatesProposals),
        },
        user: {
          id: empresaUser?.id || '',
          name: empresaUser?.name || '',
          email: empresaUser?.email || '',
          account_type: empresaUser?.account_type === 'DEMO' ? 'DEMO' : 'NORMAL',
          qtd_dias_teste: empresaUser?.qtd_dias_teste || 30,
        },
      })

      return myCompany
    },
    {
      enabled: show && !!id,
      onError: (error) => {
        console.log(error)
        toggleToast('Erro ao buscar empresa. ', 'error')
      },
    }
  )

  const closed = () => {
    formik.setValues(initialValues)
    setSettingsCompanyId(null)
    setLogoPreview('')
    setLogoDarkPreview('')
    setLogoCellPreview('')
    setUsers([])
    setCompanyStatus('')
    setActiveTab('edit')
    setHasError(false)
    setActionMessage('')
    setOriginalDemoDays(0)
    setOriginalExpires(null)
    handleClose()
  }

  const getListUser = async () => {
    if (!id) return
    try {
      setLoadingUsers(true)
      const myUsers = await getUsersAllCompany(id)
      setUsers(Array.isArray(myUsers) ? myUsers : [])
    } catch (error) {
      console.log('erro', error)
      toggleToast('Erro ao buscar lista de usuários ', 'error')
    } finally {
      setLoadingUsers(false)
    }
  }

  useEffect(() => {
    if (show) {
      setActiveTab(initialTab || 'edit')
      setCompanyStatus(data?.status || '')
      setActionMessage('')
      getListUser()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, id, initialTab])

  const toggleCompanyStatus = async () => {
    if (!id) return
    try {
      setLoading(true)
      const nextStatus = companyStatus === 'ATIVADA' ? 'DESATIVADA' : 'ATIVADA'
      await putCompanyOne(id, { status: nextStatus })
      setCompanyStatus(nextStatus)
      await getListUser()
      setActionMessage(nextStatus === 'DESATIVADA' ? 'Empresa desativada com sucesso.' : 'Empresa ativada com sucesso.')
    } catch (error) {
      console.log('erro', error)
      toggleToast('Erro ao alterar a empresa.  ', 'error')
    } finally {
      setLoading(false)
    }
  }

  const checkInputs = () => {
    const { corporateName, cnpj } = formik.values.company
    if (!corporateName || !cnpj) return false
    if (
      formik.values.user.account_type === 'DEMO' &&
      (!Number(formik.values.user.qtd_dias_teste) || Number(formik.values.user.qtd_dias_teste) <= 0)
    ) {
      return false
    }
    return true
  }

  const demoExpiresLabel = () => {
    const days = Number(formik.values.user.qtd_dias_teste) || 0
    if (formik.values.user.account_type !== 'DEMO' || days <= 0) return ''
    if (days === originalDemoDays && originalExpires) {
      const expiresAt = new Date(originalExpires)
      if (!Number.isNaN(expiresAt.getTime())) {
        return expiresAt.toLocaleDateString('pt-BR')
      }
    }
    const date = new Date()
    date.setDate(date.getDate() + days)
    return date.toLocaleDateString('pt-BR')
  }

  const changePlanQty = (field: 'qtd_plants_plano' | 'qtd_plants_plano_starter' | 'qtd_plants_gratuita', delta: number) => {
    const next = Math.max(0, Number(formik.values.plan[field] || 0) + delta)
    formik.setFieldValue(`plan.${field}`, next)
  }

  const uploadLogo = async (file: File, type: 'light' | 'dark' | 'cell') => {
    const localUrl = URL.createObjectURL(file)
    if (type === 'light') setLogoPreview(localUrl)
    else if (type === 'dark') setLogoDarkPreview(localUrl)
    else setLogoCellPreview(localUrl)

    try {
      setUploadingLogo(type)
      const formData = new FormData()
      formData.append('file', file)
      const uploaded = await postFile(formData)
      const key = uploaded?.key || ''
      if (type === 'light') {
        formik.setFieldValue('company.image', key)
        if (uploaded?.url) setLogoPreview(uploaded.url)
      } else if (type === 'dark') {
        formik.setFieldValue('company.image_dark', key)
        if (uploaded?.url) setLogoDarkPreview(uploaded.url)
      } else {
        formik.setFieldValue('company.image_cell', key)
        if (uploaded?.url) setLogoCellPreview(uploaded.url)
      }
    } catch (error) {
      console.log(error)
      toggleToast('Erro ao enviar a logo.', 'error')
    } finally {
      setUploadingLogo(null)
    }
  }

  const updateCompanySubmit = async () => {
    if (!id) return
    try {
      setLoading(true)

      const companyPayload = {
        corporateName: formik.values.company.corporateName,
        cnpj: formik.values.company.cnpj,
        site: formik.values.company.site || null,
        telephone: formik.values.company.telephone || null,
        image: formik.values.company.image || null,
        image_dark: formik.values.company.image_dark || null,
        image_cell: formik.values.company.image_cell || null,
        color_primary: formik.values.company.color_primary || null,
        color_primary_variet: formik.values.company.color_primary_variet || null,
        qtd_plants_plano: Number(formik.values.plan.qtd_plants_plano) || 0,
        qtd_plants_plano_starter: Number(formik.values.plan.qtd_plants_plano_starter) || 0,
        qtd_plants_gratuita: Number(formik.values.plan.qtd_plants_gratuita) || 0,
      }

      const settingsPayload = {
        reports_client_automatics: formik.values.settings.reports_client_automatics,
        idsTemplates: [formik.values.settings.idTemplateGeracao, formik.values.settings.idTemplateDetalhado].filter(Boolean),
        idTemplateOS: formik.values.settings.idTemplateOS || null,
        idTemplateProposal: formik.values.settings.idTemplateProposal || null,
      }

      const updates: Promise<any>[] = [
        putCompanyOne(id, companyPayload),
        settingsCompanyId
          ? putSettingsCompanyAll(settingsCompanyId, settingsPayload)
          : postSettingsCompanyOne({ ...settingsPayload, idCompany: id }),
      ]

      if (formik.values.user.id) {
        updates.push(
          updateUsersAll(formik.values.user.id, {
            account_type: formik.values.user.account_type || 'NORMAL',
            qtd_dias_teste: formik.values.user.account_type === 'DEMO' ? Number(formik.values.user.qtd_dias_teste) || 0 : null,
          })
        )
      }

      await Promise.all(updates)

      if (formik.values.user.account_type === 'DEMO') {
        setOriginalDemoDays(Number(formik.values.user.qtd_dias_teste) || 0)
        const days = Number(formik.values.user.qtd_dias_teste) || 0
        if (days !== originalDemoDays || !originalExpires) {
          const nextExpire = new Date()
          nextExpire.setDate(nextExpire.getDate() + days)
          setOriginalExpires(nextExpire.toISOString())
        }
      } else {
        setOriginalDemoDays(0)
        setOriginalExpires(null)
      }

      setActionMessage('Empresa atualizada com sucesso.')
    } catch (error: any) {
      console.log('erro', error)
      toggleToast(error?.response?.data?.message || 'Erro ao atualizar a empresa. ', 'error')
    } finally {
      setLoading(false)
    }
  }

  const submit = () => {
    setHasError(false)
    if (!checkInputs()) {
      setHasError(true)
      return
    }
    updateCompanySubmit()
  }

  const templatesGeracao = templates.filter((template) => template.type_template === 'GERACAO')
  const templatesDetalhado = templates.filter((template) => template.type_template === 'DETALHADO')

  return createPortal(
    <Modal
      id='kt_modal_edit_company'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered modal-xl'
      show={show}
      onHide={closed}
      backdrop={true}
    >
      <div className='modal-header'>
        <div className='d-flex align-items-center'>
          <div className='symbol symbol-45px symbol-circle me-4'>
            {logoPreview ? (
              <img src={logoPreview} alt='Logo da empresa' />
            ) : (
              <span className='symbol-label bg-light'>
                <KTSVG path='/media/icons/duotune/general/gen001.svg' className='svg-icon-muted svg-icon-2hx' />
              </span>
            )}
          </div>
          <h3 className='card-title align-items-start flex-column mb-0'>
            <span className='card-label fw-bold fs-3 mb-0 d-flex align-items-center'>
              {formik.values.company.corporateName || data?.corporateName || 'Empresa'}
              <span
                className={`badge badge-light-${
                  companyStatus === 'ATIVADA'
                    ? 'success'
                    : companyStatus === 'DESATIVADA'
                    ? 'danger'
                    : 'secondary'
                } fs-7 fw-semibold ms-3`}
              >
                {companyStatus || 'Não identificado'}
              </span>
            </span>
          </h3>
        </div>

        <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={closed}>
          <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
        </div>
      </div>

      <div className='px-lg-8 px-5 mt-5 border-bottom border-gray-200'>
        <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bolder flex-nowrap'>
          <li className='nav-item'>
            <a
              href='#'
              className={`nav-link d-flex align-items-center text-active-primary me-6 ${activeTab === 'edit' ? 'active' : ''}`}
              onClick={(event) => {
                event.preventDefault()
                setActiveTab('edit')
                setActionMessage('')
              }}
            >
              <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3 me-2' />
              Editar empresa
            </a>
          </li>
          <li className='nav-item'>
            <a
              href='#'
              className={`nav-link d-flex align-items-center text-active-primary me-6 ${activeTab === 'users' ? 'active' : ''}`}
              onClick={(event) => {
                event.preventDefault()
                setActiveTab('users')
                setActionMessage('')
              }}
            >
              <KTSVG path='/media/icons/duotune/general/gen019.svg' className='svg-icon-3 me-2' />
              Ativar / Desativar
            </a>
          </li>
        </ul>
      </div>

      <div className='modal-body py-lg-8 px-lg-8'>
        {activeTab === 'users' ? (
          <div>
            <h3 className='card-title align-items-start flex-column mb-6'>
              <span className='text-muted mt-1 fw-semibold fs-5'>
                Deseja <span className={`text-${companyStatus === 'ATIVADA' ? 'danger' : 'success'} fw-bold`}>{companyStatus === 'ATIVADA' ? 'DESATIVAR' : 'ATIVAR'}</span> a empresa {formik.values.company.corporateName || data?.corporateName || 'Não identificado.'} e todos os usuários?
              </span>
            </h3>

            <div style={{ height: '30rem', overflow: 'auto' }}>
              {loadingUsers ? (
                <div style={{ display: 'flex', justifyContent: 'center', height: '20rem', alignItems: 'center' }}>
                  <LoadingSpan style={{ width: '3rem', height: '3rem' }} />
                </div>
              ) : (
                <table className='table table-row-dashed table-hover table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='text-muted text-start fw-bolder fs-7 text-uppercase gs-0'>
                      <th className='min-w-150px'>Nome</th>
                      <th className='min-w-150px'>E-mail</th>
                      <th className='min-w-50px'>Tipo</th>
                      <th className='min-w-50px'>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users && users.length > 0 ? (
                      users.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <div className='d-flex align-items-center' style={{ gap: '0.5rem' }}>
                              <div className='symbol symbol-30px symbol-circle'>
                                <span
                                  className='symbol-label bg-light-primary text-primary fs-7 fw-bolder'
                                  data-bs-toggle='tooltip'
                                  title={user.name ? user.name : ''}
                                >
                                  {user.name ? initialLetters(user.name) : ''}
                                </span>
                              </div>
                              <div className='d-flex justify-content-start flex-column'>
                                <a href='#' className='text-dark fw-bold text-hover-primary fs-7'>
                                  {user.name ? user.name : 'Não encontrado. '}
                                </a>
                              </div>
                            </div>
                          </td>
                          <td>
                            <a href='#' className='text-dark fw-bold text-hover-primary d-block fs-7'>
                              {user.email ? user.email : 'Não identificado'}
                            </a>
                          </td>
                          <td>
                            <a href='#' className='text-dark fw-bold text-hover-primary d-block fs-7'>
                              {user.role && user.role.slug ? user.role.slug : 'Não identificado'}
                            </a>
                          </td>
                          <td>
                            {user.status === 'active' && (
                              <div style={{ display: 'flex', justifyContent: 'end' }}>
                                <KTSVG path='media/icons/duotune/arrows/arr016.svg' className='svg-icon-success svg-icon-2hx' />
                              </div>
                            )}
                            {user.status === 'inactive' && (
                              <div style={{ display: 'flex', justifyContent: 'end' }}>
                                <KTSVG path='media/icons/duotune/arrows/arr015.svg' className='svg-icon-danger svg-icon-2hx' />
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className='text-center py-10'>
                          <span className='text-muted fw-semibold'>Nenhum usuário encontrado.</span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : loadingCompany ? (
          <div style={{ display: 'flex', justifyContent: 'center', height: '20rem', alignItems: 'center' }}>
            <LoadingSpan style={{ width: '3rem', height: '3rem' }} />
          </div>
        ) : (
          <div className={styles.layout}>
            <aside
              className={styles.preview}
              style={{
                backgroundColor: formik.values.company.color_primary || '#ffc700',
                color: getContrastColor(formik.values.company.color_primary || '#ffc700'),
              }}
            >
              <div className={styles.previewLogos}>
                <div className={`${styles.previewLogo} ${styles.previewLogoLight}`}>
                  {logoPreview ? (
                    <img src={logoPreview} alt='Logo clara' />
                  ) : (
                    <i className='bi bi-image fs-2' style={{ opacity: 0.45 }}></i>
                  )}
                  <span className={styles.previewLogoLabel}>Logo clara</span>
                </div>
                <div className={`${styles.previewLogo} ${styles.previewLogoDark}`}>
                  {logoDarkPreview ? (
                    <img src={logoDarkPreview} alt='Logo escura' />
                  ) : (
                    <i className='bi bi-image fs-2' style={{ opacity: 0.55 }}></i>
                  )}
                  <span className={styles.previewLogoLabel}>Logo escura</span>
                </div>
                <div className={`${styles.previewLogo} ${styles.previewLogoCell}`}>
                  {logoCellPreview ? (
                    <img src={logoCellPreview} alt='Logo celular' />
                  ) : (
                    <i className='bi bi-phone fs-2' style={{ opacity: 0.45 }}></i>
                  )}
                  <span className={styles.previewLogoLabel}>Logo celular</span>
                </div>
              </div>
              <h4
                className={styles.previewName}
                style={{ color: getContrastColor(formik.values.company.color_primary || '#ffc700') }}
              >
                {formik.values.company.corporateName || 'Nome da empresa'}
              </h4>
              <p className={styles.previewMeta}>
                {formik.values.company.cnpj || '00.000.000/0000-00'}
              </p>
              <p className={styles.previewMeta}>
                {formik.values.company.site || 'www.empresa.com'}
              </p>
              <div
                className={styles.previewUser}
                style={{
                  backgroundColor: formik.values.company.color_primary_variet || '#f1bc00',
                  color: getContrastColor(formik.values.company.color_primary_variet || '#f1bc00'),
                }}
              >
                <p className={styles.previewMeta} style={{ marginBottom: '0.2rem' }}>
                  {formik.values.user.name || 'Usuário administrador'}
                </p>
                <p className={styles.previewMeta}>
                  {formik.values.user.email || 'email@empresa.com'}
                </p>
                <p className={styles.previewMeta} style={{ marginTop: '0.35rem', fontWeight: 700 }}>
                  {formik.values.user.account_type === 'DEMO'
                    ? `DEMO · ${formik.values.user.qtd_dias_teste || 0} dias · até ${demoExpiresLabel() || '-'}`
                    : 'Conta NORMAL'}
                </p>
              </div>
            </aside>

            <div className={styles.sections}>
              <section className={styles.section}>
                <div className={styles.sectionTitle}>
                  <span className={styles.badge}>1</span>
                  <div>
                    <div className='fw-bold fs-6'>Empresa</div>
                    <div className='text-muted fs-8'>Dados principais da empresa</div>
                  </div>
                </div>
                <div className={styles.grid2}>
                  <div>
                    <label className='form-label required fw-bold fs-7'>Nome da Empresa</label>
                    <input
                      type='text'
                      className='form-control form-control-solid bg-body'
                      placeholder='Minha Empresa'
                      value={formik.values.company.corporateName}
                      onChange={formik.handleChange('company.corporateName')}
                    />
                    {!formik.values.company.corporateName && hasError && (
                      <div className={styles.error}>O nome da empresa é obrigatório.</div>
                    )}
                  </div>
                  <div>
                    <label className='form-label required fw-bold fs-7'>CNPJ</label>
                    <input
                      type='text'
                      className='form-control form-control-solid bg-body'
                      placeholder='00.000.000/0000-00'
                      value={formik.values.company.cnpj}
                      onChange={(event) => formik.setFieldValue('company.cnpj', maskCnpj(event.target.value))}
                    />
                    {!formik.values.company.cnpj && hasError && (
                      <div className={styles.error}>O CNPJ da empresa é obrigatório.</div>
                    )}
                  </div>
                  <div>
                    <label className='form-label fw-bold fs-7'>Site</label>
                    <input
                      type='text'
                      className='form-control form-control-solid bg-body'
                      placeholder='www.empresa.com'
                      value={formik.values.company.site}
                      onChange={formik.handleChange('company.site')}
                    />
                  </div>
                  <div>
                    <label className='form-label fw-bold fs-7'>Telefone</label>
                    <input
                      type='text'
                      className='form-control form-control-solid bg-body'
                      placeholder='(00) 00000-0000'
                      value={formik.values.company.telephone}
                      onChange={formik.handleChange('company.telephone')}
                    />
                  </div>
                </div>
              </section>

              <section className={styles.section}>
                <div className={styles.sectionTitle}>
                  <span className={styles.badge}>2</span>
                  <div>
                    <div className='fw-bold fs-6'>Tipo de conta</div>
                    <div className='text-muted fs-8'>Altera o usuário EMPRESA vinculado</div>
                  </div>
                </div>
                <div className={styles.accountTypeWrap}>
                  <div className={styles.accountTypeGrid}>
                    <button
                      type='button'
                      className={`${styles.accountTypeBtn} bg-body ${formik.values.user.account_type === 'NORMAL' ? styles.accountTypeBtnActive : ''}`}
                      onClick={() => formik.setFieldValue('user.account_type', 'NORMAL')}
                    >
                      <span className={styles.accountTypeRadio} />
                      <span className={styles.accountTypeContent}>
                        <span className={styles.accountTypeTitle}>NORMAL</span>
                        <span className={styles.accountTypeDesc}>Conta definitiva, sem data de expiração</span>
                      </span>
                    </button>
                    <button
                      type='button'
                      className={`${styles.accountTypeBtn} bg-body ${formik.values.user.account_type === 'DEMO' ? styles.accountTypeBtnDemo : ''}`}
                      onClick={() => {
                        formik.setFieldValue('user.account_type', 'DEMO')
                        if (!Number(formik.values.user.qtd_dias_teste) || Number(formik.values.user.qtd_dias_teste) <= 0) {
                          formik.setFieldValue('user.qtd_dias_teste', 30)
                        }
                      }}
                    >
                      <span className={styles.accountTypeRadio} />
                      <span className={styles.accountTypeContent}>
                        <span className={styles.accountTypeTitleRow}>
                          <span className={styles.accountTypeTitle}>DEMO</span>
                          <span className={styles.accountTypeBadge}>Teste</span>
                        </span>
                        <span className={styles.accountTypeDesc}>Período limitado. Expira automaticamente</span>
                      </span>
                    </button>
                  </div>

                  {formik.values.user.account_type === 'DEMO' && (
                    <div className={styles.demoPanel}>
                      <div className={styles.demoDays}>
                        <label className='form-label required fw-bold fs-7'>Dias de teste</label>
                        <div className={styles.planCounter}>
                          <button
                            type='button'
                            className='btn btn-sm btn-light'
                            onClick={() => formik.setFieldValue('user.qtd_dias_teste', Math.max(1, Number(formik.values.user.qtd_dias_teste || 0) - 1))}
                          >
                            -
                          </button>
                          <input
                            type='number'
                            min={1}
                            className='form-control form-control-solid bg-body'
                            placeholder='30'
                            value={formik.values.user.qtd_dias_teste}
                            onChange={formik.handleChange('user.qtd_dias_teste')}
                          />
                          <button
                            type='button'
                            className='btn btn-sm btn-light'
                            onClick={() => formik.setFieldValue('user.qtd_dias_teste', Number(formik.values.user.qtd_dias_teste || 0) + 1)}
                          >
                            +
                          </button>
                        </div>
                        <div className={styles.demoPresets}>
                          {[15, 30, 60, 90].map((days) => (
                            <button
                              key={days}
                              type='button'
                              className={`${styles.demoPreset} ${Number(formik.values.user.qtd_dias_teste) === days ? styles.demoPresetActive : ''}`}
                              onClick={() => formik.setFieldValue('user.qtd_dias_teste', days)}
                            >
                              {days} dias
                            </button>
                          ))}
                        </div>
                        {(!Number(formik.values.user.qtd_dias_teste) || Number(formik.values.user.qtd_dias_teste) <= 0) && hasError && (
                          <div className={styles.error}>Informe a quantidade de dias de teste.</div>
                        )}
                      </div>
                      <div className={styles.demoExpire}>
                        <span className={styles.demoExpireLabel}>Expira em</span>
                        <span className={styles.demoExpireDate}>{demoExpiresLabel() || '-'}</span>
                      </div>
                    </div>
                  )}
                </div>
              </section>

              <section className={styles.section}>
                <div className={styles.sectionTitle}>
                  <span className={styles.badge}>3</span>
                  <div>
                    <div className='fw-bold fs-6'>Identidade visual</div>
                    <div className='text-muted fs-8'>Logos e cores da marca</div>
                  </div>
                </div>
                <div className={styles.grid3}>
                  <label className={`${styles.logoBox} bg-body`}>
                    {logoPreview ? (
                      <img src={logoPreview} alt='Logo clara' />
                    ) : (
                      <i className='bi bi-image fs-2 text-muted'></i>
                    )}
                    <span className='fw-bold fs-7'>Logo clara</span>
                    <span className='text-muted fs-8'>
                      {uploadingLogo === 'light' ? 'Enviando...' : 'Clique para enviar'}
                    </span>
                    <input
                      type='file'
                      accept='image/*'
                      disabled={uploadingLogo !== null}
                      onChange={(event) => {
                        const file = event.target.files?.[0]
                        if (file) uploadLogo(file, 'light')
                        event.target.value = ''
                      }}
                    />
                  </label>
                  <label className={`${styles.logoBox} ${styles.logoBoxDark}`}>
                    {logoDarkPreview ? (
                      <img src={logoDarkPreview} alt='Logo escura' />
                    ) : (
                      <i className='bi bi-image fs-2 text-white'></i>
                    )}
                    <span className='fw-bold fs-7 text-white'>Logo escura</span>
                    <span className='text-white-50 fs-8'>
                      {uploadingLogo === 'dark' ? 'Enviando...' : 'Clique para enviar'}
                    </span>
                    <input
                      type='file'
                      accept='image/*'
                      disabled={uploadingLogo !== null}
                      onChange={(event) => {
                        const file = event.target.files?.[0]
                        if (file) uploadLogo(file, 'dark')
                        event.target.value = ''
                      }}
                    />
                  </label>
                  <label className={`${styles.logoBox} bg-body`}>
                    {logoCellPreview ? (
                      <img src={logoCellPreview} alt='Logo celular' />
                    ) : (
                      <i className='bi bi-phone fs-2 text-muted'></i>
                    )}
                    <span className='fw-bold fs-7'>Logo celular</span>
                    <span className='text-muted fs-8'>
                      {uploadingLogo === 'cell' ? 'Enviando...' : 'Clique para enviar'}
                    </span>
                    <input
                      type='file'
                      accept='image/*'
                      disabled={uploadingLogo !== null}
                      onChange={(event) => {
                        const file = event.target.files?.[0]
                        if (file) uploadLogo(file, 'cell')
                        event.target.value = ''
                      }}
                    />
                  </label>
                </div>
                <div className={styles.grid2} style={{ marginTop: '0.85rem' }}>
                  <div className={`${styles.colorRow} bg-body`}>
                    <input
                      type='color'
                      value={formik.values.company.color_primary}
                      onChange={formik.handleChange('company.color_primary')}
                    />
                    <div>
                      <div className='fw-bold fs-7'>Cor primária</div>
                      <div className='text-muted fs-8'>{formik.values.company.color_primary}</div>
                    </div>
                  </div>
                  <div className={`${styles.colorRow} bg-body`}>
                    <input
                      type='color'
                      value={formik.values.company.color_primary_variet}
                      onChange={formik.handleChange('company.color_primary_variet')}
                    />
                    <div>
                      <div className='fw-bold fs-7'>Cor variante</div>
                      <div className='text-muted fs-8'>{formik.values.company.color_primary_variet}</div>
                    </div>
                  </div>
                </div>
              </section>

              <section className={styles.section}>
                <div className={styles.sectionTitle}>
                  <span className={styles.badge}>4</span>
                  <div>
                    <div className='fw-bold fs-6'>Plano da empresa</div>
                    <div className='text-muted fs-8'>Quantidade de usinas por tipo de plano</div>
                  </div>
                </div>
                <div className={styles.grid3}>
                  {[
                    { label: 'PREMIUM', field: 'qtd_plants_plano' as const, color: '#7239EA' },
                    { label: 'STARTER', field: 'qtd_plants_plano_starter' as const, color: '#009EF7' },
                    { label: 'BÁSICAS', field: 'qtd_plants_gratuita' as const, color: '#50CD89' },
                  ].map((plan) => (
                    <div className={`${styles.planCard} bg-body`} key={plan.field}>
                      <div className={styles.planHead}>
                        <span className='fw-bold fs-7'>{plan.label}</span>
                        <span style={{ backgroundColor: plan.color, width: 10, height: 10, borderRadius: 99, display: 'inline-block' }}></span>
                      </div>
                      <div className={styles.planCounter}>
                        <button
                          type='button'
                          className='btn btn-sm btn-light'
                          onClick={() => changePlanQty(plan.field, -1)}
                        >
                          -
                        </button>
                        <input
                          type='number'
                          min={0}
                          className='form-control form-control-solid bg-body'
                          value={formik.values.plan[plan.field]}
                          onChange={formik.handleChange(`plan.${plan.field}`)}
                        />
                        <button
                          type='button'
                          className='btn btn-sm btn-light'
                          onClick={() => changePlanQty(plan.field, 1)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className={styles.section}>
                <div className={styles.sectionTitle}>
                  <span className={styles.badge}>5</span>
                  <div>
                    <div className='fw-bold fs-6'>Preferências</div>
                    <div className='text-muted fs-8'>Templates e automações</div>
                  </div>
                </div>
                <div className={styles.grid2}>
                  <label className={`${styles.toggleCard} ${styles.toggleCardFull} bg-body`}>
                    <span>
                      <span className='fw-bold fs-7 d-block'>Relatórios automáticos</span>
                      <span className='text-muted fs-8'>Disponibilizar relatórios para os clientes</span>
                    </span>
                    <div className='form-check form-switch form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={formik.values.settings.reports_client_automatics}
                        onChange={formik.handleChange('settings.reports_client_automatics')}
                      />
                    </div>
                  </label>
                  {/* TODO: reativar módulo WhatsApp
                  <label className={`${styles.toggleCard} bg-body`}>
                    <span>
                      <span className='fw-bold fs-7 d-block'>Liberar WhatsApp</span>
                      <span className='text-muted fs-8'>Habilitar o módulo de WhatsApp da empresa</span>
                    </span>
                    <div className='form-check form-switch form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={formik.values.settings.liberar_whatsapp}
                        onChange={formik.handleChange('settings.liberar_whatsapp')}
                      />
                    </div>
                  </label>
                  */}
                  <div>
                    <label className='form-label fw-bold fs-7'>Template de geração</label>
                    <select
                      className='form-select form-select-solid bg-body'
                      value={formik.values.settings.idTemplateGeracao}
                      onChange={formik.handleChange('settings.idTemplateGeracao')}
                    >
                      <option value=''>Padrão público</option>
                      {templatesGeracao.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className='form-label fw-bold fs-7'>Template detalhado</label>
                    <select
                      className='form-select form-select-solid bg-body'
                      value={formik.values.settings.idTemplateDetalhado}
                      onChange={formik.handleChange('settings.idTemplateDetalhado')}
                    >
                      <option value=''>Padrão público</option>
                      {templatesDetalhado.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className='form-label fw-bold fs-7'>Template de O.S.</label>
                    <select
                      className='form-select form-select-solid bg-body'
                      value={formik.values.settings.idTemplateOS}
                      onChange={formik.handleChange('settings.idTemplateOS')}
                    >
                      <option value=''>Padrão público</option>
                      {templatesOS.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className='form-label fw-bold fs-7'>Template de proposta</label>
                    <select
                      className='form-select form-select-solid bg-body'
                      value={formik.values.settings.idTemplateProposal}
                      onChange={formik.handleChange('settings.idTemplateProposal')}
                    >
                      <option value=''>Padrão público</option>
                      {templatesProposal.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.description}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </section>
            </div>
          </div>
        )}
      </div>

      <div className='modal-footer'>
        {activeTab === 'users' ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <button
              type='button'
              className='btn btn-lg btn-secondary'
              disabled={loading}
              onClick={closed}
            >
              <KTSVG path='/media/icons/duotune/arrows/arr063.svg' className='svg-icon-3 ms-2 me-0' />
              <span style={{ marginLeft: '0.5rem' }}>Cancelar</span>
            </button>
            <div className='d-flex align-items-center' style={{ gap: '0.75rem' }}>
              {actionMessage && (
                <span className='badge badge-success text-white fs-7 fw-semibold'>{actionMessage}</span>
              )}
              <button
                type='button'
                className={`btn btn-lg btn-${companyStatus === 'ATIVADA' ? 'danger' : 'success'}`}
                disabled={loading || loadingUsers}
                onClick={toggleCompanyStatus}
              >
                {companyStatus === 'ATIVADA' ? 'Desativar' : 'Ativar'}
                {loading ? (
                  <LoadingSpan style={{ marginLeft: '0.5rem' }} />
                ) : (
                  <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-3 ms-2 me-0' />
                )}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <button
              type='button'
              className='btn btn-lg btn-secondary'
              disabled={loading}
              onClick={closed}
            >
              <KTSVG path='/media/icons/duotune/arrows/arr063.svg' className='svg-icon-3 ms-2 me-0' />
              <span style={{ marginLeft: '0.5rem' }}>Cancelar</span>
            </button>
            <div className='d-flex align-items-center' style={{ gap: '0.75rem' }}>
              {actionMessage && (
                <span className='badge badge-success text-white fs-7 fw-semibold'>{actionMessage}</span>
              )}
              <button
                type='button'
                className='btn btn-lg btn-primary'
                disabled={loading || loadingCompany}
                onClick={submit}
              >
                Editar{' '}
                {loading ? (
                  <LoadingSpan style={{ marginLeft: '0.5rem' }} />
                ) : (
                  <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-3 ms-2 me-0' />
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>,
    modalsRoot
  )
}

export { ModalUpdateCompany }
